import React, { useState, useEffect } from 'react';
import { 
  db, 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc, 
  Timestamp,
  writeBatch 
} from '../../firebase';
import { 
  Calendar, 
  Clock, 
  BookOpen, 
  FileText, 
  X, 
  AlertCircle, 
  Check,
  RefreshCw,
  AlertTriangle,
  User
} from 'lucide-react';
import styles from '../CSS/CreateReservationModal.module.css';

const CreateReservationModal = ({ 
  user, 
  space, 
  onClose,
  canCreatePeriodicReservations = false,
  canDelegateReservations = false
}) => {
  // Toggle entre manual y periódica
  const [reservationType, setReservationType] = useState('manual');
  
  // Estados para reserva manual
  const [date, setDate] = useState('');
  const [timeStart, setTimeStart] = useState('');
  const [timeEnd, setTimeEnd] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  
  // Estados para reserva periódica
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);
  
  // Estados comunes
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingReservations, setExistingReservations] = useState([]);
  const [availableStartTimes, setAvailableStartTimes] = useState([]);
  const [availableEndTimes, setAvailableEndTimes] = useState([]);
  
  // Estados para conflictos periódicos
  const [conflicts, setConflicts] = useState([]);
  const [conflictResolution, setConflictResolution] = useState('skip');
  const [showConflictWarning, setShowConflictWarning] = useState(false);

  // Estados para delegación de usuario
  const [delegatedUser, setDelegatedUser] = useState(null);
  const [delegatedUserSearch, setDelegatedUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [manualDelegation, setManualDelegation] = useState({
    name: '',
    email: ''
  });
  const [delegationMode, setDelegationMode] = useState('none'); // 'none', 'search', 'manual'

  const TITLE_MAX_LENGTH = 100;
  const DESCRIPTION_MAX_LENGTH = 350;

  // 🆕 URL DEL BACKEND EN RENDER
  const API_BASE_URL = 'https://truebavirtual.onrender.com';

  const daysOfWeek = [
    { id: 1, name: 'Lunes', short: 'L' },
    { id: 2, name: 'Martes', short: 'M' },
    { id: 3, name: 'Miércoles', short: 'X' },
    { id: 4, name: 'Jueves', short: 'J' },
    { id: 5, name: 'Viernes', short: 'V' },
  ];

  const generateTimeOptions = () => {
    const options = [];
    let hour = 8;
    let minute = 0;
    
    while (hour < 20 || (hour === 20 && minute === 0)) {
      const hh = String(hour).padStart(2, '0');
      const mm = String(minute).padStart(2, '0');
      options.push(`${hh}:${mm}`);
      
      minute += 10;
      if (minute >= 60) {
        hour++;
        minute = 0;
      }
    }
    return options;
  };

  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const hasOverlap = (start1, end1, start2, end2) => {
    const start1Mins = timeToMinutes(start1);
    const end1Mins = timeToMinutes(end1);
    const start2Mins = timeToMinutes(start2);
    const end2Mins = timeToMinutes(end2);
    return start1Mins < end2Mins && end1Mins > start2Mins;
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    if (value.length <= TITLE_MAX_LENGTH) {
      setTitle(value);
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= DESCRIPTION_MAX_LENGTH) {
      setDescription(value);
    }
  };

  const toggleDay = (dayId) => {
    setSelectedDays(prev => 
      prev.includes(dayId) 
        ? prev.filter(id => id !== dayId)
        : [...prev, dayId].sort()
    );
  };

  const calculateOccurrences = () => {
    if (!startDate || !endDate || selectedDays.length === 0) return [];
    
    const occurrences = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
      
      if (selectedDays.includes(adjustedDay)) {
        occurrences.push(new Date(d).toISOString().split('T')[0]);
      }
    }
    
    return occurrences;
  };

  const occurrences = reservationType === 'periodic' ? calculateOccurrences() : [];

  // Función para buscar usuarios
  const searchUsers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const usersRef = collection(db, 'users');
      const searchLower = searchTerm.toLowerCase();
      
      const querySnapshot = await getDocs(usersRef);
      const results = [];
      
      console.log('🔍 Buscando usuarios con término:', searchTerm);
      
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        
        // Construir nombre completo
        const firstName = userData.firstName || '';
        const lastName = userData.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        // 🆕 CORRECCIÓN CRÍTICA: El email ES el ID del documento
        const email = doc.id;
        
        console.log(`📄 Usuario encontrado:`, {
          docId: doc.id,
          firstName,
          lastName,
          email
        });
        
        // Buscar en firstName, lastName, nombre completo y email
        const firstNameLower = firstName.toLowerCase();
        const lastNameLower = lastName.toLowerCase();
        const fullNameLower = fullName.toLowerCase();
        const emailLower = email.toLowerCase();
        
        if (firstNameLower.includes(searchLower) || 
            lastNameLower.includes(searchLower) || 
            fullNameLower.includes(searchLower) || 
            emailLower.includes(searchLower)) {
          
          // Solo incluir usuarios con firstName
          if (firstName) {
            const userResult = {
              uid: doc.id,
              firstName: firstName,
              lastName: lastName,
              displayName: fullName || email,
              email: email
            };
            
            console.log('✅ Usuario agregado a resultados:', userResult);
            results.push(userResult);
          }
        }
      });
      
      // Ordenar por nombre
      results.sort((a, b) => a.displayName.localeCompare(b.displayName));
      
      console.log(`📊 Total resultados: ${results.length}`);
      setSearchResults(results.slice(0, 5));
    } catch (error) {
      console.error('❌ Error buscando usuarios:', error);
      setFeedback('Error al buscar usuarios');
      setFeedbackType('error');
    } finally {
      setIsSearching(false);
    }
  };

  // 🔧 FUNCIÓN CORREGIDA PARA ENVIAR EMAILS DE NOTIFICACIÓN
  const sendReservationEmails = async (reservationData) => {
    try {
      const creatorName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
      const creatorEmail = user.email;
      
      console.log('📧 === INICIANDO ENVÍO DE EMAILS ===');
      console.log('👤 Creador:', { creatorName, creatorEmail });
      console.log('🔧 Modo de delegación:', delegationMode);
      
      let assignedName = null;
      let assignedEmail = null;
      
      // ⭐ EXTRAER DATOS DEL USUARIO ASIGNADO
      if (delegationMode === 'search' && delegatedUser) {
        assignedName = delegatedUser.displayName;
        assignedEmail = delegatedUser.email;
        console.log('✅ Usuario asignado (búsqueda):', { assignedName, assignedEmail });
      } else if (delegationMode === 'manual' && manualDelegation.name) {
        assignedName = manualDelegation.name;
        assignedEmail = manualDelegation.email || null;
        console.log('✅ Usuario asignado (manual):', { assignedName, assignedEmail });
      } else {
        console.log('ℹ️ Sin usuario asignado');
      }

      const formatDate = (dateStr) => {
        if (!dateStr) return 'No especificada';
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-ES', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      };

      // ============================================
      // 1. SIEMPRE ENVIAR EMAIL AL CREADOR
      // ============================================
      const creatorEmailData = {
        creatorEmail: creatorEmail,
        creatorName: creatorName,
        reservationId: reservationData.id || `RES-${Date.now()}`,
        spaceName: space.name || 'Espacio',
        title: title || 'Sin título',  // ✅ Título separado
        description: description || '', // ✅ Descripción separada (puede estar vacía)
        date: reservationType === 'manual' ? formatDate(date) : null,
        startDate: reservationType === 'periodic' ? formatDate(startDate) : null,
        endDate: reservationType === 'periodic' ? formatDate(endDate) : null,
        timeStart: timeStart || 'No especificado',
        timeEnd: timeEnd || 'No especificado',
        isPeriodic: reservationType === 'periodic'
      };

      console.log('📤 Enviando email de confirmación al creador...');
      console.log('📋 Datos del email:', creatorEmailData);
      
      try {
        const creatorResponse = await fetch(`${API_BASE_URL}/api/reservations/notify-created`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(creatorEmailData)
        });

        const creatorResult = await creatorResponse.json();
        
        if (!creatorResponse.ok) {
          console.error('❌ Error en respuesta del servidor:', creatorResult);
          console.warn('⚠️ No se pudo enviar email al creador');
        } else {
          console.log('✅ Email enviado al creador exitosamente:', creatorResult.message);
        }
      } catch (creatorError) {
        console.error('❌ Error al enviar email al creador:', creatorError);
      }

      // ============================================
      // 2. SI HAY ASIGNADO CON EMAIL Y ES DIFERENTE AL CREADOR
      // ============================================
      if (assignedEmail && assignedEmail !== creatorEmail) {
        console.log('📤 Enviando email al usuario asignado...');
        
        const assignedEmailData = {
          assignedEmail: assignedEmail,
          assignedName: assignedName,
          creatorName: creatorName,
          reservationId: reservationData.id || `RES-${Date.now()}`,
          spaceName: space.name || 'Espacio',
          title: title || 'Sin título',  // ✅ Título separado
          description: description || '', // ✅ Descripción separada (puede estar vacía)
          date: reservationType === 'manual' ? formatDate(date) : null,
          startDate: reservationType === 'periodic' ? formatDate(startDate) : null,
          endDate: reservationType === 'periodic' ? formatDate(endDate) : null,
          timeStart: timeStart || 'No especificado',
          timeEnd: timeEnd || 'No especificado',
          isPeriodic: reservationType === 'periodic'
        };

        console.log('📋 Datos del email de asignación:', assignedEmailData);
        
        try {
          const assignedResponse = await fetch(`${API_BASE_URL}/api/reservations/notify-assigned`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(assignedEmailData)
          });

          const assignedResult = await assignedResponse.json();
          
          if (!assignedResponse.ok) {
            console.error('❌ Error en respuesta del servidor:', assignedResult);
            console.warn('⚠️ No se pudo enviar email al asignado');
          } else {
            console.log('✅ Email enviado al asignado exitosamente:', assignedResult.message);
          }
        } catch (assignedError) {
          console.error('❌ Error al enviar email al asignado:', assignedError);
        }
      } else {
        // Mensajes informativos de por qué no se envió
        if (!assignedEmail) {
          console.log('ℹ️ No se enviará email al asignado (sin email proporcionado)');
        } else if (assignedEmail === creatorEmail) {
          console.log('ℹ️ El asignado es el mismo creador, no se envía email duplicado');
        }
      }
      
      console.log('📧 === ENVÍO DE EMAILS COMPLETADO ===');
      
    } catch (error) {
      console.error('❌ Error general al enviar emails:', error);
    }
  };

  // useEffect para la búsqueda con debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (delegationMode === 'search') {
        searchUsers(delegatedUserSearch);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [delegatedUserSearch, delegationMode]);

  // useEffect para cargar reservas existentes
  useEffect(() => {
    const fetchExistingReservations = async () => {
      if (reservationType === 'manual' && (!date || !space)) return;

      const reservationsRef = collection(db, 'reservations');
      const q = query(
        reservationsRef,
        where('space', '==', space.name),
        where('date', '==', date)
      );

      try {
        const querySnapshot = await getDocs(q);
        const reservations = [];
        querySnapshot.forEach((doc) => {
          reservations.push(doc.data());
        });
        setExistingReservations(reservations);
      } catch (error) {
        console.error('Error al cargar reservas:', error);
        setFeedback('Error al cargar horarios disponibles');
        setFeedbackType('error');
      }
    };

    if (reservationType === 'manual') {
      fetchExistingReservations();
    }
  }, [date, space, reservationType]);

  // useEffect para detectar conflictos en reservas periódicas
  useEffect(() => {
    const detectConflicts = async () => {
      if (reservationType !== 'periodic' || occurrences.length === 0 || !timeStart || !timeEnd || !space) {
        if (conflicts.length > 0) {
          setConflicts([]);
          setShowConflictWarning(false);
        }
        return;
      }

      try {
        const reservationsRef = collection(db, 'reservations');
        const detectedConflicts = [];

        for (const occurrence of occurrences) {
          const q = query(
            reservationsRef,
            where('space', '==', space.name),
            where('date', '==', occurrence)
          );

          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            const reservation = doc.data();
            if (hasOverlap(timeStart, timeEnd, reservation.startTime, reservation.endTime)) {
              detectedConflicts.push({
                date: occurrence,
                existingTitle: reservation.title,
                existingTime: `${reservation.startTime} - ${reservation.endTime}`,
                reservationId: doc.id
              });
            }
          });
        }

        setConflicts(detectedConflicts);
        setShowConflictWarning(detectedConflicts.length > 0);
      } catch (error) {
        console.error('Error al detectar conflictos:', error);
      }
    };

    detectConflicts();
  }, [reservationType, startDate, endDate, selectedDays, timeStart, timeEnd]);

  // useEffect para calcular horas disponibles
  useEffect(() => {
    const allTimes = generateTimeOptions();
    
    if (reservationType === 'manual') {
      const availableStarts = allTimes.filter(time => {
        const currentTimeMinutes = timeToMinutes(time);
        
        return !existingReservations.some(reservation => {
          const reservationStartMinutes = timeToMinutes(reservation.startTime);
          const reservationEndMinutes = timeToMinutes(reservation.endTime);
          const nextTimeSlotMinutes = currentTimeMinutes + 10;
          
          return (
            (currentTimeMinutes >= reservationStartMinutes && currentTimeMinutes < reservationEndMinutes) ||
            (nextTimeSlotMinutes > reservationStartMinutes && currentTimeMinutes < reservationStartMinutes) ||
            (currentTimeMinutes < reservationEndMinutes && nextTimeSlotMinutes > reservationStartMinutes)
          );
        });
      });

      setAvailableStartTimes(availableStarts);
      
      if (timeStart) {
        const startTimeMinutes = timeToMinutes(timeStart);
        
        const availableEnds = allTimes.filter(time => {
          const endTimeMinutes = timeToMinutes(time);
          if (endTimeMinutes <= startTimeMinutes) return false;
          
          return !existingReservations.some(reservation => {
            const reservationStartMinutes = timeToMinutes(reservation.startTime);
            const reservationEndMinutes = timeToMinutes(reservation.endTime);
            return (startTimeMinutes < reservationEndMinutes && endTimeMinutes > reservationStartMinutes);
          });
        });
        
        setAvailableEndTimes(availableEnds);
      } else {
        setAvailableEndTimes([]);
      }
    } else {
      setAvailableStartTimes(allTimes);
      if (timeStart) {
        setAvailableEndTimes(allTimes.filter(time => time > timeStart));
      } else {
        setAvailableEndTimes([]);
      }
    }
  }, [existingReservations, timeStart, reservationType]);

  // useEffect para resetear timeEnd cuando cambie timeStart
  useEffect(() => {
    setTimeEnd('');
  }, [timeStart]);

  const handleCreateManualReservation = async () => {
    if (!user) {
      setFeedback('Debes iniciar sesión para crear una reserva.');
      setFeedbackType('error');
      return;
    }
    if (!space) {
      setFeedback('No se ha seleccionado un espacio.');
      setFeedbackType('error');
      return;
    }
    if (!date || !timeStart || !timeEnd || !title.trim()) {
      setFeedback('Por favor, completa todos los campos requeridos.');
      setFeedbackType('error');
      return;
    }

    // Validación de delegación manual
    if (delegationMode === 'manual' && !manualDelegation.name.trim()) {
      setFeedback('Por favor, ingresa el nombre del usuario delegado.');
      setFeedbackType('error');
      return;
    }

    const hasConflict = existingReservations.some(reservation =>
      hasOverlap(timeStart, timeEnd, reservation.startTime, reservation.endTime)
    );

    if (hasConflict) {
      setFeedback('El horario seleccionado se solapa con una reserva existente.');
      setFeedbackType('error');
      return;
    }

    setIsSubmitting(true);

    const reservationData = {
      type: 'manual',
      space: space.name,
      date,
      startTime: timeStart,
      endTime: timeEnd,
      title: title.trim(),
      description: description.trim(),
      reservedBy: {
        uid: user.uid,
        displayName: user.displayName,
        email: user.email
      },
      ...(delegationMode !== 'none' && {
        delegatedTo: delegationMode === 'search' 
          ? {
              uid: delegatedUser.uid,
              displayName: delegatedUser.displayName,
              email: delegatedUser.email,
              delegationType: 'database'
            }
          : {
              displayName: manualDelegation.name.trim(),
              email: manualDelegation.email.trim(),
              delegationType: 'manual'
            }
      }),
      createdAt: Timestamp.now()
    };

    try {
      const docRef = doc(collection(db, 'reservations'));
      await setDoc(docRef, reservationData);
      
      // 🔧 FIX: Pasar el objeto completo con el ID
      await sendReservationEmails({
        ...reservationData,
        id: docRef.id
      });
      
      setFeedback('¡Reserva creada con éxito! Se han enviado las notificaciones por email.');
      setFeedbackType('success');
      
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Error al crear reserva:', error);
      setFeedback('Error al crear la reserva. Por favor, inténtalo de nuevo.');
      setFeedbackType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreatePeriodicReservation = async () => {
    if (!user) {
      setFeedback('Debes iniciar sesión para crear una reserva.');
      setFeedbackType('error');
      return;
    }
    if (!space) {
      setFeedback('No se ha seleccionado un espacio.');
      setFeedbackType('error');
      return;
    }
    if (!startDate || !endDate || !timeStart || !timeEnd || !title.trim() || selectedDays.length === 0) {
      setFeedback('Por favor, completa todos los campos requeridos.');
      setFeedbackType('error');
      return;
    }

    // Validación de delegación manual
    if (delegationMode === 'manual' && !manualDelegation.name.trim()) {
      setFeedback('Por favor, ingresa el nombre del usuario delegado.');
      setFeedbackType('error');
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      setFeedback('La fecha de fin debe ser posterior a la fecha de inicio.');
      setFeedbackType('error');
      return;
    }

    if (occurrences.length === 0) {
      setFeedback('No hay fechas válidas con los días seleccionados.');
      setFeedbackType('error');
      return;
    }

    setIsSubmitting(true);

    try {
      const batch = writeBatch(db);
      const datesToCreate = conflictResolution === 'skip' 
        ? occurrences.filter(date => !conflicts.find(c => c.date === date))
        : occurrences;

      if (conflictResolution === 'overwrite' && conflicts.length > 0) {
        for (const conflict of conflicts) {
          const conflictRef = doc(db, 'reservations', conflict.reservationId);
          batch.delete(conflictRef);
        }
      }

      const delegationData = delegationMode !== 'none' 
        ? {
            delegatedTo: delegationMode === 'search' 
              ? {
                  uid: delegatedUser.uid,
                  displayName: delegatedUser.displayName,
                  email: delegatedUser.email,
                  delegationType: 'database'
                }
              : {
                  displayName: manualDelegation.name.trim(),
                  email: manualDelegation.email.trim(),
                  delegationType: 'manual'
                }
          }
        : {};

      const periodicDocRef = doc(collection(db, 'periodicReservations'));
      batch.set(periodicDocRef, {
        type: 'periodic',
        space: space.name,
        startDate,
        endDate,
        startTime: timeStart,
        endTime: timeEnd,
        title: title.trim(),
        description: description.trim(),
        daysOfWeek: selectedDays,
        conflictResolution,
        excludedDates: conflictResolution === 'skip' ? conflicts.map(c => c.date) : [],
        overwrittenDates: conflictResolution === 'overwrite' ? conflicts.map(c => c.date) : [],
        occurrencesCount: datesToCreate.length,
        reservedBy: {
          uid: user.uid,
          displayName: user.displayName,
          email: user.email
        },
        ...delegationData,
        createdAt: Timestamp.now(),
        periodicId: periodicDocRef.id
      });

      for (const occurrence of datesToCreate) {
        const reservationRef = doc(collection(db, 'reservations'));
        batch.set(reservationRef, {
          type: 'periodic',
          periodicId: periodicDocRef.id,
          space: space.name,
          date: occurrence,
          startTime: timeStart,
          endTime: timeEnd,
          title: title.trim(),
          description: description.trim(),
          reservedBy: {
            uid: user.uid,
            displayName: user.displayName,
            email: user.email
          },
          ...delegationData,
          createdAt: Timestamp.now()
        });
      }

      await batch.commit();

      // 🔧 FIX CRÍTICO: Pasar todos los datos necesarios en lugar de un objeto incompleto
      const periodicReservationData = {
        id: periodicDocRef.id,
        type: 'periodic',
        space: space.name,
        startDate,
        endDate,
        startTime: timeStart,
        endTime: timeEnd,
        title: title.trim(),
        description: description.trim(),
        daysOfWeek: selectedDays,
        reservedBy: {
          uid: user.uid,
          displayName: user.displayName,
          email: user.email
        },
        ...delegationData
      };

      await sendReservationEmails(periodicReservationData);

      setFeedback(`¡Reserva periódica creada! ${datesToCreate.length} sesiones programadas. Se han enviado las notificaciones por email.`);
      setFeedbackType('success');
      
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error al crear reserva periódica:', error);
      setFeedback('Error al crear la reserva. Por favor, inténtalo de nuevo.');
      setFeedbackType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (reservationType === 'manual') {
      handleCreateManualReservation();
    } else {
      handleCreatePeriodicReservation();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            Crear Reserva
            <span className={styles.highlight}>{space?.name}</span>
          </h2>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={24} />
          </button>
        </div>

        <div className={styles.formContainer}>
          {/* Toggle de tipo de reserva */}
          {canCreatePeriodicReservations && (
            <div className={styles.reservationTypeToggle}>
              <button
                type="button"
                className={`${styles.toggleButton} ${reservationType === 'manual' ? styles.toggleButtonActive : ''}`}
                onClick={() => {
                  setReservationType('manual');
                  setConflicts([]);
                  setShowConflictWarning(false);
                }}
              >
                <Calendar size={18} />
                Reserva Manual
              </button>
              <button
                type="button"
                className={`${styles.toggleButton} ${reservationType === 'periodic' ? styles.toggleButtonActive : ''}`}
                onClick={() => {
                  setReservationType('periodic');
                  setDate('');
                  setExistingReservations([]);
                }}
              >
                <RefreshCw size={18} />
                Reserva Periódica
              </button>
            </div>
          )}

          {reservationType === 'manual' ? (
            <>
              {/* Fecha */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  <Calendar className={styles.inputIcon} />
                  Fecha <span className={styles.requiredMarker}>*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={styles.formInput}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Horas */}
              <div className={styles.timeInputs}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <Clock className={styles.inputIcon} />
                    Hora Inicio <span className={styles.requiredMarker}>*</span>
                  </label>
                  <select
                    value={timeStart}
                    onChange={(e) => {
                      setTimeStart(e.target.value);
                      setTimeEnd('');
                    }}
                    className={styles.formInput}
                    disabled={!date}
                  >
                    <option value="">Selecciona hora</option>
                    {availableStartTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <Clock className={styles.inputIcon} />
                    Hora Fin <span className={styles.requiredMarker}>*</span>
                  </label>
                  <select
                    value={timeEnd}
                    onChange={(e) => setTimeEnd(e.target.value)}
                    className={styles.formInput}
                    disabled={!timeStart}
                  >
                    <option value="">Selecciona hora</option>
                    {availableEndTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Rango de fechas */}
              <div className={styles.dateRangeContainer}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <Calendar className={styles.inputIcon} />
                    Fecha Inicio <span className={styles.requiredMarker}>*</span>
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={styles.formInput}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <Calendar className={styles.inputIcon} />
                    Fecha Fin <span className={styles.requiredMarker}>*</span>
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={styles.formInput}
                    min={startDate || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              {/* Días de la semana */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Días de la semana <span className={styles.requiredMarker}>*</span>
                </label>
                <div className={styles.daysContainer}>
                  {daysOfWeek.map(day => (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => toggleDay(day.id)}
                      className={`${styles.dayButton} ${selectedDays.includes(day.id) ? styles.dayButtonActive : ''}`}
                    >
                      <span className={styles.dayShort}>{day.short}</span>
                      <span className={styles.dayFull}>{day.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Horas para periódica */}
              <div className={styles.timeInputs}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <Clock className={styles.inputIcon} />
                    Hora Inicio <span className={styles.requiredMarker}>*</span>
                  </label>
                  <select
                    value={timeStart}
                    onChange={(e) => setTimeStart(e.target.value)}
                    className={styles.formInput}
                  >
                    <option value="">Selecciona hora</option>
                    {availableStartTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <Clock className={styles.inputIcon} />
                    Hora Fin <span className={styles.requiredMarker}>*</span>
                  </label>
                  <select
                    value={timeEnd}
                    onChange={(e) => setTimeEnd(e.target.value)}
                    className={styles.formInput}
                    disabled={!timeStart}
                  >
                    <option value="">Selecciona hora</option>
                    {availableEndTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Resumen de ocurrencias */}
              {occurrences.length > 0 && (
                <div className={styles.summaryBox}>
                  <div className={styles.summaryTitle}>
                    📅 Resumen de la reserva periódica
                  </div>
                  <div className={styles.summaryDetail}>
                    Se crearán {occurrences.length} sesiones entre {new Date(startDate).toLocaleDateString('es-ES')} y {new Date(endDate).toLocaleDateString('es-ES')}
                  </div>
                </div>
              )}

              {/* Advertencia de conflictos */}
              {showConflictWarning && conflicts.length > 0 && (
                <div className={styles.conflictWarning}>
                  <div className={styles.conflictHeader}>
                    <AlertTriangle size={18} />
                    <span className={styles.conflictTitle}>
                      ⚠️ {conflicts.length} conflictos detectados
                    </span>
                  </div>
                  <div className={styles.conflictList}>
                    {conflicts.map((conflict, index) => (
                      <div key={index} className={styles.conflictItem}>
                        • {new Date(conflict.date).toLocaleDateString('es-ES')}: {conflict.existingTitle} ({conflict.existingTime})
                      </div>
                    ))}
                  </div>
                  <div className={styles.conflictResolutionContainer}>
                    <span className={styles.conflictResolutionTitle}>
                      ¿Cómo deseas proceder?
                    </span>
                    <div className={styles.radioGroup}>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="conflictResolution"
                          value="skip"
                          checked={conflictResolution === 'skip'}
                          onChange={(e) => setConflictResolution(e.target.value)}
                          className={styles.radioInput}
                        />
                        <div>
                          <div className={styles.radioTitle}>Omitir fechas con conflictos</div>
                          <div className={styles.radioDescription}>
                            Solo se crearán las {occurrences.length - conflicts.length} sesiones sin conflictos
                          </div>
                        </div>
                      </label>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          name="conflictResolution"
                          value="overwrite"
                          checked={conflictResolution === 'overwrite'}
                          onChange={(e) => setConflictResolution(e.target.value)}
                          className={styles.radioInput}
                        />
                        <div>
                          <div className={styles.radioTitle}>Sobrescribir reservas existentes</div>
                          <div className={styles.radioDescription}>
                            Se eliminarán las {conflicts.length} reservas en conflicto y se crearán todas las {occurrences.length} sesiones nuevas
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Título */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <BookOpen className={styles.inputIcon} />
              Título <span className={styles.requiredMarker}>*</span>
              <span className={styles.characterCount}>
                {title.length}/{TITLE_MAX_LENGTH}
              </span>
            </label>
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              className={styles.formInput}
              placeholder="Ej. Matemáticas 2°ESO"
              maxLength={TITLE_MAX_LENGTH}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FileText className={styles.inputIcon} />
              Descripción (opcional)
              <span className={styles.characterCount}>
                {description.length}/{DESCRIPTION_MAX_LENGTH}
              </span>
            </label>
            <textarea
              value={description}
              onChange={handleDescriptionChange}
              className={`${styles.formInput} ${styles.textarea}`}
              placeholder="Información adicional sobre la reserva"
              rows={3}
              maxLength={DESCRIPTION_MAX_LENGTH}
            />
          </div>

          {/* Sección de Delegación de Usuario */}
          {canDelegateReservations && (
            <div className={styles.delegationSection}>
              <div className={styles.delegationHeader}>
                <label className={styles.formLabel}>
                  <User className={styles.inputIcon} />
                  Asignar Reserva
                </label>
                <p className={styles.delegationDescription}>
                  Asigna esta reserva a otro usuario
                </p>
              </div>

              <div className={styles.delegationModes}>
                <button
                  type="button"
                  className={`${styles.delegationModeBtn} ${delegationMode === 'none' ? styles.delegationModeActive : ''}`}
                  onClick={() => {
                    setDelegationMode('none');
                    setDelegatedUser(null);
                    setDelegatedUserSearch('');
                    setManualDelegation({ name: '', email: '' });
                    setSearchResults([]);
                  }}
                >
                  Sin asignar
                </button>
                <button
                  type="button"
                  className={`${styles.delegationModeBtn} ${delegationMode === 'search' ? styles.delegationModeActive : ''}`}
                  onClick={() => {
                    setDelegationMode('search');
                    setManualDelegation({ name: '', email: '' });
                  }}
                >
                  Buscar usuario
                </button>
                <button
                  type="button"
                  className={`${styles.delegationModeBtn} ${delegationMode === 'manual' ? styles.delegationModeActive : ''}`}
                  onClick={() => {
                    setDelegationMode('manual');
                    setDelegatedUser(null);
                    setDelegatedUserSearch('');
                    setSearchResults([]);
                  }}
                >
                  Entrada manual
                </button>
              </div>

              {delegationMode === 'search' && (
                <div className={styles.userSearchContainer}>
                  <div className={styles.formGroup}>
                    <input
                      type="text"
                      value={delegatedUserSearch}
                      onChange={(e) => setDelegatedUserSearch(e.target.value)}
                      className={styles.formInput}
                      placeholder="Buscar por nombre..."
                    />
                  </div>

                  {isSearching && (
                    <div className={styles.searchingIndicator}>
                      Buscando usuarios...
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className={styles.searchResults}>
                      {searchResults.map((result) => (
                        <button
                          key={result.uid}
                          type="button"
                          className={`${styles.userResultItem} ${delegatedUser?.uid === result.uid ? styles.userResultSelected : ''}`}
                          onClick={() => {
                            setDelegatedUser(result);
                            setDelegatedUserSearch(result.displayName);
                            setSearchResults([]);
                          }}
                        >
                          <User size={16} className={styles.userResultIcon} />
                          <div className={styles.userResultInfo}>
                            <span className={styles.userResultName}>{result.displayName}</span>
                            <span className={styles.userResultEmail}>{result.email}</span>
                          </div>
                          {delegatedUser?.uid === result.uid && (
                            <Check size={16} className={styles.userResultCheck} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {delegatedUser && (
                    <div className={styles.selectedUserBadge}>
                      <User size={14} />
                      <span>Asignado a: {delegatedUser.displayName}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setDelegatedUser(null);
                          setDelegatedUserSearch('');
                        }}
                        className={styles.removeUserBtn}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {delegationMode === 'manual' && (
                <div className={styles.manualDelegationContainer}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Nombre del usuario <span className={styles.requiredMarker}>*</span>
                    </label>
                    <input
                      type="text"
                      value={manualDelegation.name}
                      onChange={(e) => setManualDelegation(prev => ({ ...prev, name: e.target.value }))}
                      className={styles.formInput}
                      placeholder="Nombre completo"
                      maxLength={100}
                    />
                  </div>

                  {/* 🆕 CAMPO DE EMAIL OPCIONAL */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Email (opcional)
                    </label>
                    <input
                      type="email"
                      value={manualDelegation.email}
                      onChange={(e) => setManualDelegation(prev => ({ ...prev, email: e.target.value }))}
                      className={styles.formInput}
                      placeholder="correo@ejemplo.com"
                    />
                    <p className={styles.emailNote}>
                      Si proporcionas el email, el usuario recibirá una notificación
                    </p>
                  </div>

                  {manualDelegation.name && (
                    <div className={styles.manualDelegationPreview}>
                      <User size={14} />
                      <span>
                        Se asignará a: {manualDelegation.name}
                        {manualDelegation.email && ` (${manualDelegation.email})`}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {feedback && (
            <div className={`${styles.feedback} ${styles[feedbackType]}`}>
              {feedbackType === 'error' ? (
                <AlertCircle className={styles.feedbackIcon} />
              ) : (
                <Check className={styles.feedbackIcon} />
              )}
              {feedback}
            </div>
          )}

          <div className={styles.modalActions}>
            <button onClick={onClose} className={styles.btnSecondary}>
              Cancelar
            </button>
            <button 
              onClick={handleSubmit}
              className={styles.btnPrimary}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creando...' : reservationType === 'periodic' ? 'Crear Reserva Periódica' : 'Crear Reserva'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateReservationModal;